import { useState } from "react";
import { X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds";

const FONT_SERIF = "font-['Playfair_Display']";
const FONT_SANS = "font-['DM_Sans']";
const INPUT = "w-full bg-white border border-[#E5DFD3] px-4 py-3 text-[#1A1F2C] placeholder-[#A39B8C] focus:outline-none focus:border-[#B5462F] transition-colors";

export default function DemoRequestModal({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/demo-requests", form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1F2C]/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid={LANDING.demoModal}
        className={`bg-[#F8F5EE] w-full max-w-lg shadow-2xl border border-[#E5DFD3] ${FONT_SANS}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-8 pt-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#B5462F]">Artisan by Pica9</p>
            <h3 className={`${FONT_SERIF} text-3xl tracking-tight mt-1 text-[#1A1F2C]`}>
              Request a demo
            </h3>
          </div>
          <button
            data-testid={LANDING.demoClose}
            onClick={onClose}
            className="p-1 text-[#6B6358] hover:text-[#1A1F2C] transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        {done ? (
          <div data-testid={LANDING.demoSuccess} className="px-8 py-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#1F6B6B]/12 text-[#1F6B6B] flex items-center justify-center mx-auto">
              <Check size={26} />
            </div>
            <h4 className={`${FONT_SERIF} text-2xl mt-5 text-[#1A1F2C]`}>You&rsquo;re on the list.</h4>
            <p className="text-[#6B6358] mt-2 leading-relaxed">
              Thanks, {form.name.split(" ")[0] || "there"}. Our team will reach out shortly to
              schedule your walkthrough.
            </p>
            <button
              onClick={onClose}
              className="mt-7 px-7 py-3 bg-[#1A1F2C] text-[#F8F5EE] hover:bg-[#2A2F3C] transition-colors tracking-wide text-sm"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-8 py-7 space-y-4">
            <Field label="Full name">
              <input
                data-testid={LANDING.demoName}
                required value={form.name} onChange={set("name")}
                className={INPUT} placeholder="Jane Marketer"
              />
            </Field>
            <Field label="Work email">
              <input
                data-testid={LANDING.demoEmail}
                required type="email" value={form.email} onChange={set("email")}
                className={INPUT} placeholder="jane@brand.com"
              />
            </Field>
            <Field label="Company">
              <input
                data-testid={LANDING.demoCompany}
                value={form.company} onChange={set("company")}
                className={INPUT} placeholder="Brand, Inc."
              />
            </Field>
            <Field label="What would you like to solve? (optional)">
              <textarea
                data-testid={LANDING.demoMessage}
                value={form.message} onChange={set("message")} rows={3}
                className={`${INPUT} resize-none`}
                placeholder="Localizing 400 dealer flyers a month while staying on-brand…"
              />
            </Field>
            {error && <p className="text-sm text-[#B5462F]">{error}</p>}
            <button
              data-testid={LANDING.demoSubmit}
              type="submit" disabled={busy}
              className="w-full px-7 py-4 bg-[#B5462F] text-white hover:bg-[#9A3B27] transition-colors tracking-wide text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Sending…" : "Request a Demo"}
            </button>
            <p className="text-xs text-[#6B6358] text-center">
              We&rsquo;ll only use your details to arrange your demo.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
